import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { toast } from 'react-toastify';
import { openFilePreview } from '../common/FilePreviewModal';
import EmailModal from '../common/EmailModal';
import TableScrollContainer from '../common/TableScrollContainer';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import '../documentation/JobList.css';
import useSortableTable from '../common/useSortableTable';

const API_BASE = '/api/documentation/jobs';
const MASTERS_API = '/api/masters';
const DEFAULT_CLIENT_REPORT_FIELDS = [
  'idf_no',
  'mbl_no',
  'invoice_no',
  'supplier_name',
  'total_containers_size',
  'weight',
  'packages',
  'vessel_name',
  'eta',
  'etb',
  'duty_paid',
  'wharfage_paid',
  'remarks',
  'icd',
];

const toDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value).toUpperCase();
};

const CopyCell = ({ text, children }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
    if (!val || val === '-') return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(val).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = val;
      ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
      document.body.removeChild(ta);
    }
  };
  return (
    <span className="copy-cell">
      {children}
      <button
        type="button"
        className={`copy-icon-btn${copied ? ' copied' : ''}`}
        title={copied ? 'Copied!' : 'Copy'}
        onClick={handleCopy}
        onMouseDown={e => e.stopPropagation()}
      >
        {copied
          ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
          : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
        }
      </button>
    </span>
  );
};

const getFieldLabel = (field, group) => {
  const shipmentType = (group?.shipment_type || '').toUpperCase();
  if (field.key === 'mbl_no') {
    if (shipmentType.includes('AIR')) return 'AWB';
    if (shipmentType.includes('ROAD')) return 'RCN';
    return 'BL';
  }
  if (field.key === 'icd') {
    if (shipmentType.includes('AIR')) return 'TERMINAL';
    if (shipmentType.includes('ROAD')) return 'BORDER';
    return 'ICD';
  }
  if (field.key === 'total_containers_size' && shipmentType.includes('AIR')) return 'WEIGHT';
  if (field.key === 'vessel_name') {
    if (shipmentType.includes('SEA')) return 'VESSEL';
    if (shipmentType.includes('AIR')) return 'AIRLINE';
    if (shipmentType.includes('ROAD')) return 'VEHICLE';
  }
  return String(field.label || '').toUpperCase();
};

const HIDDEN_FOR_AIR_ROAD = ['etb', 'wharfage_paid', 'carry_in_end_date'];
const HIDDEN_FOR_EXPORT_SEA = ['eta', 'etb', 'duty_paid', 'wharfage_paid', 'icd', 'total_containers_size'];
const ONLY_FOR_EXPORT_SEA = ['weight', 'packages'];

const getVisibleFields = (fields, shipmentType) => {
  const st = (shipmentType || '').toUpperCase();
  if (st === 'EXPORT-SEA') {
    return fields.filter(f => !HIDDEN_FOR_EXPORT_SEA.includes(f.key));
  }
  const hidden = [...HIDDEN_FOR_AIR_ROAD, ...ONLY_FOR_EXPORT_SEA];
  if (st.endsWith('AIR') || st === 'BORDER') {
    return fields.filter(f => !hidden.includes(f.key));
  }
  if (st.endsWith('ROAD')) {
    return fields.filter(f => !hidden.includes(f.key) && f.key !== 'total_containers_size');
  }
  return fields.filter(f => !ONLY_FOR_EXPORT_SEA.includes(f.key));
};

const renderCell = (row, field) => {
  const value = row[field.key];
  if (field.key === 'supplier_name') {
    if (value === null || value === undefined || value === '') return '-';
    const full = String(value);
    const words = full.toUpperCase().split(/\s+/).filter(Boolean);
    const short = words.slice(0, 2).join(' ');
    return <span title={full}>{short}</span>;
  }
  return toDisplay(value);
};

function formatDailyDate(dateStr, createdAt) {
  const pad = n => n.toString().padStart(2, '0');
  let datePart = dateStr || '';
  if (datePart) {
    const d = new Date(datePart);
    if (!isNaN(d)) datePart = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  let timePart = '';
  if (createdAt) {
    const t = new Date(createdAt);
    if (!isNaN(t)) timePart = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
  }
  return { datePart, timePart };
}

const todayISO = () => new Date().toISOString().split('T')[0];

const addDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const FILTER_MODES = [
  { id: 'daily', label: 'Daily' },
  { id: 'minus1d', label: '-1D' },
  { id: 'minus1w', label: '-1W' },
  { id: 'minus1m', label: '-1M' },
  { id: 'minus3m', label: '-3M' },
  { id: 'range', label: 'Date Range' },
];

const DailyClientReportsTab = ({ customerId, hasPermission, onCountChange, refreshTrigger, canDeleteClientReport }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterMode, setFilterMode] = useState('daily');
  const [filterDateFrom, setFilterDateFrom] = useState(todayISO());
  const [filterDateTo, setFilterDateTo] = useState(todayISO());
  const [emailReport, setEmailReport] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleViewPdf = useCallback(async (row) => {
    try {
      const res = await fetch(row.view_pdf_url, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        throw new Error('Failed to load PDF preview.');
      }
      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        throw new Error('The generated PDF file is empty.');
      }
      const blobUrl = URL.createObjectURL(blob);
      const previewWindow = openFilePreview(blobUrl, `Daily_Client_Report_${row.date}.pdf`);
      if (previewWindow && previewWindow.addEventListener) {
        previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
      } else {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not open PDF preview.');
    }
  }, []);

  const handleDownloadFile = useCallback(async (url, filename) => {
    if (!url) return;
    try {
      const res = await fetch(url, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        throw new Error('Failed to download file.');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not download file.');
    }
  }, []);

  const fetchDailyReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customerId) params.append('customer_id', customerId);
      if (filterDateFrom) params.append('from_date', filterDateFrom);
      if (filterDateTo) params.append('to_date', filterDateTo);
      const res = await fetch(`${API_BASE}/daily-client-reports/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch daily client reports.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to fetch daily client reports.');
    } finally {
      setLoading(false);
    }
  }, [customerId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchDailyReports();
  }, [fetchDailyReports]);

  useEffect(() => {
    if (refreshTrigger) {
      fetchDailyReports();
    }
  }, [refreshTrigger, fetchDailyReports]);

  useEffect(() => {
    if (onCountChange) onCountChange(rows.length);
  }, [rows, onCountChange]);

  const { items: sortedRows, requestSort, getSortIndicator } = useSortableTable(rows, { key: 'created_at', direction: 'descending' });

  // Sync date range when a preset mode is selected
  useEffect(() => {
    const today = todayISO();
    if (filterMode === 'minus1d') {
      setFilterDateFrom(addDays(today, -1));
      setFilterDateTo(today);
    } else if (filterMode === 'minus1w') {
      setFilterDateFrom(addDays(today, -6));
      setFilterDateTo(today);
    } else if (filterMode === 'minus1m') {
      setFilterDateFrom(addDays(today, -29));
      setFilterDateTo(today);
    } else if (filterMode === 'minus3m') {
      setFilterDateFrom(addDays(today, -89));
      setFilterDateTo(today);
    } else if (filterMode === 'daily') {
      setFilterDateFrom(today);
      setFilterDateTo(today);
    }
  }, [filterMode]);

  const handleClearFilters = () => {
    setFilterMode('daily');
    setFilterDateFrom(todayISO());
    setFilterDateTo(todayISO());
  };

  const sendDailyReportEmail = async (emailData) => {
    if (!emailReport) return;
    if (!emailData.selectedTemplateId) {
      throw new Error('Please select an email template.');
    }
    setSendingEmail(true);
    try {
      const res = await fetch(`${API_BASE}/daily-client-reports/${emailReport.id}/send-email/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          template_id: emailData.selectedTemplateId,
          cc_email: emailData.cc_email || emailData.cc || '',
          body: emailData.body || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send Daily Client Report email.');
      toast.success(data.message || 'Daily Client Report email sent.');
      setEmailReport(null);
    } finally {
      setSendingEmail(false);
    }
  };

  if (!hasPermission('CLIENT REPORTS', 'can_view')) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Daily Client Reports.</p></div>;
  }

  return (
    <div>
      <div className="toolbar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>VIEW MODE</label>
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #ced4da' }}>
            {FILTER_MODES.map(mode => (
              <button
                key={mode.id}
                onClick={() => setFilterMode(mode.id)}
                style={{
                  padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                  background: filterMode === mode.id ? '#1a73e8' : '#fff',
                  color: filterMode === mode.id ? '#fff' : '#333',
                  fontWeight: filterMode === mode.id ? 600 : 400,
                }}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {filterMode !== 'daily' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>FROM DATE</label>
              <input
                type="date"
                className="search-input"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                readOnly={filterMode !== 'range'}
                title="From Date"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>TO DATE</label>
              <input
                type="date"
                className="search-input"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                readOnly={filterMode !== 'range'}
                title="To Date"
              />
            </div>
          </>
        )}

        <button className="add-btn" style={{ background: '#6c757d' }} onClick={handleClearFilters}>Clear Date Filter</button>
      </div>

      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>GENERATED DATE {getSortIndicator('created_at')}</th>
              <th onClick={() => requestSort('customer')} style={{ cursor: 'pointer' }}>CUSTOMER {getSortIndicator('customer')}</th>
              <th>VIEW PDF</th>
              <th>DOWNLOAD PDF</th>
              <th>DOWNLOAD EXCEL</th>
              <th onClick={() => requestSort('email_sent_at')} style={{ cursor: 'pointer' }}>EMAIL SENT {getSortIndicator('email_sent_at')}</th>
              {canDeleteClientReport && <th>EMAIL</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={canDeleteClientReport ? 7 : 6} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={canDeleteClientReport ? 7 : 6} className="no-data">No daily reports found.</td></tr>)}
            {!loading && sortedRows.map(r => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(() => {
                    const { datePart, timePart } = formatDailyDate(r.created_at, r.created_at);
                    return (
                      <CopyCell text={r.created_at}>
                        <span>
                          {datePart} {timePart}<br />
                          <span style={{ fontSize: 11, color: '#555' }}>{r.generated_by || '-'}</span>
                        </span>
                      </CopyCell>
                    );
                  })()}
                </td>
                <td><CopyCell text={r.customer}>{r.customer || '-'}</CopyCell></td>
                <td>
                  <button
                    type="button"
                    className="icon-btn"
                    title="View PDF"
                    onClick={() => handleViewPdf(r)}
                  >
                    <i className="fa fa-eye" />
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Download PDF"
                    onClick={() => handleDownloadFile(r.download_pdf_url, `${r.title || 'Daily_Client_Report'}_${r.date}.pdf`)}
                  >
                    <i className="fa fa-download" />
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Download Excel"
                    onClick={() => handleDownloadFile(r.download_excel_url, `${r.title || 'Daily_Client_Report'}_${r.date}.xlsx`)}
                  >
                    <i className="fa fa-download" />
                  </button>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.email_sent_at ? (() => {
                    const { datePart, timePart } = formatDailyDate(r.email_sent_at, r.email_sent_at);
                    return (
                      <CopyCell text={r.email_sent_at}>
                        <span>
                          {datePart} {timePart}<br />
                          <span style={{ fontSize: 11, color: '#555' }}>{r.email_sent_by || '-'}</span>
                        </span>
                      </CopyCell>
                    );
                  })() : '-'}
                </td>
                {canDeleteClientReport && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={!r.client_email}
                    title={r.client_email ? `Email ${r.client_email}` : 'No client email configured'}
                    onClick={() => setEmailReport(r)}
                  >
                    <i className="fa fa-envelope" /> Email
                  </button>
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
      {emailReport && (
        <EmailModal
          isOpen={!!emailReport}
          onClose={() => { if (!sendingEmail) setEmailReport(null); }}
          onSave={sendDailyReportEmail}
          defaultTo={emailReport.client_email}
          jobData={{ customer_name: emailReport.customer }}
          showAttachments={false}
          fixedAttachment={{ name: `${emailReport.title || 'Daily Client Report'}.pdf`, url: emailReport.download_pdf_url }}
          restrictToTemplateNames={['DAILY CLIENT REPORT', 'CLIENT DAILY REPORT']}
          subjectBuilder={(template) => `${emailReport.customer} - ${template.subject || ''} - ${formatDailyDate(emailReport.date).datePart}`}
        />
      )}
    </div>
  );
};

const ClientReports = ({ onCountChange }) => {
  const { hasPermission } = usePermissions();
  const canEditClientReport = hasPermission('CLIENT REPORTS', 'can_edit') || hasPermission('ADMIN CLIENT REPORTS', 'can_edit');
  const canDeleteClientReport = hasPermission('CLIENT REPORTS', 'can_delete') || hasPermission('ADMIN CLIENT REPORTS', 'can_delete');

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [tab, setTab] = useState('current');
  const [dailyReportCount, setDailyReportCount] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [fields, setFields] = useState([]);
  const [allFieldDefs, setAllFieldDefs] = useState([]);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);

  const [showConfig, setShowConfig] = useState(false);
  const [configCustomerId, setConfigCustomerId] = useState('');
  const [configCustomerName, setConfigCustomerName] = useState('');
  const [configFields, setConfigFields] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [dragSrcKey, setDragSrcKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const [rowDragSrcId, setRowDragSrcId] = useState(null);
  const [rowDragOverId, setRowDragOverId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [generatedTodayIds, setGeneratedTodayIds] = useState(new Set());

  const fetchCustomers = useCallback(async () => {
    try {
      let linkedCustomerIds = new Set();
      try {
        const usersRes = await fetch('/api/admin/users/', { headers: getAuthHeaders(false) });
        if (usersRes.ok) {
          const users = await usersRes.json();
          linkedCustomerIds = new Set(
            (users || [])
              .filter(u => u.profile?.user_type === 'CUSTOMER' && u.profile?.linked_customer)
              .map(u => u.profile.linked_customer)
          );
        }
      } catch (e) { console.error('Failed to load customer users', e); }

      const res = await fetch(`${MASTERS_API}/customers/?page_size=1000`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.results || []);
      setCustomers(
        list
          .filter(c => linkedCustomerIds.has(c.id))
          .map(c => ({ id: String(c.id), text: c.name, name: c.name }))
      );
    } catch (e) {
      toast.error(e.message || 'Failed to load customers');
    }
  }, []);

  const fetchAllFieldDefs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/client-report-fields/`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch field definitions');
      const data = await res.json();
      setAllFieldDefs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load field definitions');
    }
  }, []);

  const fetchReport = useCallback(async () => {
    if (!customerId && !appliedSearch) {
      setGroups([]);
      setFields([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customerId) params.append('customer_id', customerId);
      params.append('tab', 'all');
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (appliedSearch) params.append('search', appliedSearch);
      const res = await fetch(`${API_BASE}/client-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch client report');
      const data = await res.json();
      if (data.customer) {
        setCustomerId(String(data.customer.id));
        setCustomerName(data.customer.name || '');
      }
      setGroups(data.groups || []);
      setFields(data.fields || []);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch client report');
      setGroups([]);
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, fromDate, toDate, appliedSearch]);

  const fetchConfig = useCallback(async (custId) => {
    if (!custId) return;
    try {
      const res = await fetch(`${API_BASE}/client-report-config/?customer_id=${custId}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch configuration');
      const data = await res.json();
      setConfigFields(data.fields || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load configuration');
      setConfigFields(DEFAULT_CLIENT_REPORT_FIELDS.filter(key => allFieldDefs.some(field => field.key === key)));
    }
  }, [allFieldDefs]);

  const fetchGeneratedToday = useCallback(async () => {
    const today = todayISO();
    try {
      const params = new URLSearchParams({ from_date: today, to_date: today });
      const res = await fetch(`${API_BASE}/daily-client-reports/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch generated daily reports');
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.results || []);
      const generatedIds = new Set();
      rows.forEach(r => {
        if (r.customer_id) {
          generatedIds.add(String(r.customer_id));
        } else if (r.customer) {
          const match = customers.find(c => c.name === r.customer || c.text === r.customer);
          if (match) generatedIds.add(String(match.id));
        }
      });
      setGeneratedTodayIds(generatedIds);
    } catch (e) {
      console.error('Error fetching generated daily reports:', e);
    }
  }, [customers]);

  useEffect(() => {
    fetchGeneratedToday();
  }, [fetchGeneratedToday]);

  useEffect(() => {
    fetchCustomers();
    fetchAllFieldDefs();
  }, [fetchCustomers, fetchAllFieldDefs]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (!onCountChange) return;
    if (tab === 'dailyReport') {
      onCountChange(dailyReportCount);
    } else {
      const count = (groups || []).reduce((sum, g) => {
        const rows = g.rows || [];
        const activeRows = rows.filter(r => (tab === 'current' ? !r.is_released : r.is_released));
        return sum + activeRows.length;
      }, 0);
      onCountChange(count);
    }
  }, [groups, tab, dailyReportCount, onCountChange]);

  const handleCustomerChange = (val) => {
    const selected = customers.find(c => String(c.id) === String(val));
    setCustomerId(val || '');
    setCustomerName(selected ? selected.name : '');
  };

  const handleApplySearch = () => setAppliedSearch(search);

  const handleRefresh = () => {
    fetchCustomers();
    fetchAllFieldDefs();
    fetchReport();
    setRefreshTrigger(prev => prev + 1);
  };
  const handleClearFilters = () => {
    setCustomerId('');
    setCustomerName('');
    setFromDate('');
    setToDate('');
    setSearch('');
    setAppliedSearch('');
    setTab('current');
    setGroups([]);
    setFields([]);
  };

  const handlePreviewDailyReport = async () => {
    if (!customerId) { toast.error('Please select a client first.'); return; }
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('customer_id', customerId);
      params.append('tab', tab);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (appliedSearch) params.append('search', appliedSearch);
      const res = await fetch(`${API_BASE}/preview-daily-client-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate preview.');
      }
      const blob = await res.blob();
      if (!blob.size) throw new Error('The preview PDF is empty.');
      const blobUrl = URL.createObjectURL(blob);
      openFilePreview(blobUrl, `Preview_Client_Report_${customerId}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      toast.error(e.message || 'Failed to generate preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerateDailyReport = async () => {
    if (!customerId) { toast.error('Please select a client first.'); return; }
    setGenerateLoading(true);
    try {
      const res = await fetch(`${API_BASE}/generate-daily-client-report/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          customer_id: customerId,
          tab,
          from_date: fromDate,
          to_date: toDate,
          search: appliedSearch,
          report_date: new Date().toISOString().split('T')[0],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate daily report.');
      }
      toast.success('Daily Client Report generated and saved successfully.');
      setTab('dailyReport');
      setGeneratedTodayIds(prev => new Set([...prev, customerId]));
      setRefreshTrigger(prev => prev + 1);
      fetchGeneratedToday();
    } catch (e) {
      toast.error(e.message || 'Failed to generate daily report.');
    } finally {
      setGenerateLoading(false);
    }
  };

  const openConfig = () => {
    const targetId = customerId || '';
    setConfigCustomerId(targetId);
    const selected = customers.find(c => String(c.id) === String(targetId));
    setConfigCustomerName(selected ? selected.name : '');
    setShowConfig(true);
    if (targetId) fetchConfig(targetId);
    else setConfigFields(DEFAULT_CLIENT_REPORT_FIELDS.filter(key => allFieldDefs.some(field => field.key === key)));
  };

  const closeConfig = () => {
    setShowConfig(false);
    setConfigFields([]);
    setConfigCustomerId('');
    setConfigCustomerName('');
  };

  const handleConfigCustomerChange = (val) => {
    const selected = customers.find(c => String(c.id) === String(val));
    setConfigCustomerId(val || '');
    setConfigCustomerName(selected ? selected.name : '');
    if (val) fetchConfig(val);
  };

  const toggleField = (key) => {
    setConfigFields(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleDragStart = useCallback((e, key) => {
    setDragSrcKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  }, []);

  const handleDragOver = useCallback((e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key !== dragSrcKey) setDragOverKey(key);
  }, [dragSrcKey]);

  const handleDragEnd = useCallback(() => {
    setDragSrcKey(null);
    setDragOverKey(null);
  }, []);

  const handleDrop = useCallback((e, targetKey) => {
    e.preventDefault();
    const srcKey = e.dataTransfer.getData('text/plain') || dragSrcKey;
    if (!srcKey || srcKey === targetKey) { handleDragEnd(); return; }

    setConfigFields(prev => {
      if (!prev.includes(srcKey) || !prev.includes(targetKey)) return prev;
      const arr = [...prev];
      const srcIdx = arr.indexOf(srcKey);
      const tgtIdx = arr.indexOf(targetKey);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      const movingDown = srcIdx < tgtIdx;
      const [moved] = arr.splice(srcIdx, 1);
      const newTgtIdx = arr.indexOf(targetKey);
      arr.splice(movingDown ? newTgtIdx + 1 : newTgtIdx, 0, moved);
      return arr;
    });
    handleDragEnd();
  }, [dragSrcKey]);

  const handleSaveConfig = async () => {
    if (!configCustomerId) { toast.error('Please select a client to configure.'); return; }
    setSavingConfig(true);
    try {
      const res = await fetch(`${API_BASE}/client-report-config/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ customer_id: configCustomerId, fields: configFields }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save configuration.');
      }
      toast.success('Client report configuration saved.');
      if (String(configCustomerId) === String(customerId)) {
        await fetchReport();
      } else {
        setCustomerId(configCustomerId);
        setCustomerName(configCustomerName);
      }
      closeConfig();
    } catch (e) {
      toast.error(e.message || 'Failed to save configuration.');
    } finally {
      setSavingConfig(false);
    }
  };

  // Client Report row order is persisted independently of the Port/Airport/Border Report screens.
  const getRowSortEndpoint = (shipmentType) => {
    const st = (shipmentType || '').toUpperCase();
    if (st.endsWith('AIR') || st.endsWith('ROAD') || st === 'BORDER' || st.endsWith('SEA')) {
      return 'update-client-report-sort-order';
    }
    return null;
  };

  const canDragRows = canEditClientReport && tab !== 'dailyReport';

  const handleRowDragStart = useCallback((e, rowId) => {
    setRowDragSrcId(rowId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleRowDragOver = useCallback((e, rowId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (rowId !== rowDragSrcId) setRowDragOverId(rowId);
  }, [rowDragSrcId]);

  const handleRowDragEnd = useCallback(() => {
    setRowDragSrcId(null);
    setRowDragOverId(null);
  }, []);

  const handleRowDrop = useCallback(async (e, group, targetId) => {
    e.preventDefault();
    const endpoint = getRowSortEndpoint(group.shipment_type);
    if (!endpoint || !rowDragSrcId || rowDragSrcId === targetId) { handleRowDragEnd(); return; }

    const displayedRows = group.rows || [];
    const srcIdx = displayedRows.findIndex(r => r.id === rowDragSrcId);
    const tgtIdx = displayedRows.findIndex(r => r.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) { handleRowDragEnd(); return; }

    const movingDown = srcIdx < tgtIdx;
    const newDisplayedRows = [...displayedRows];
    const [srcRow] = newDisplayedRows.splice(srcIdx, 1);
    const newTgtIdx = newDisplayedRows.findIndex(r => r.id === targetId);
    newDisplayedRows.splice(movingDown ? newTgtIdx + 1 : newTgtIdx, 0, srcRow);

    // Merge the reordered visible subset back into the full group rows,
    // preserving the relative positions of rows not shown on the current tab.
    setGroups(prevGroups => prevGroups.map(g => {
      if (g.shipment_type !== group.shipment_type) return g;
      const fullRows = g.rows || [];
      const displayedIds = new Set(displayedRows.map(r => r.id));
      const matchIndices = [];
      fullRows.forEach((r, i) => { if (displayedIds.has(r.id)) matchIndices.push(i); });
      const newFullRows = [...fullRows];
      matchIndices.forEach((fullIdx, k) => { newFullRows[fullIdx] = newDisplayedRows[k]; });
      return { ...g, rows: newFullRows };
    }));
    handleRowDragEnd();

    const orders = newDisplayedRows.map((r, i) => ({ id: r.id, sort_order: i }));
    try {
      const res = await fetch(`${API_BASE}/${endpoint}/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orders }),
      });
      if (!res.ok) throw new Error('Failed to save row order.');
    } catch (err) {
      toast.error('Failed to save row order.');
      fetchReport();
    }
  }, [rowDragSrcId, handleRowDragEnd, fetchReport]);

  const customerOptions = useMemo(() => [{ id: '', text: 'Select Customer' }, ...customers], [customers]);

  const reportCustomerOptions = useMemo(() => {
    const filtered = tab !== 'current'
      ? customers
      : customers.filter(c => !generatedTodayIds.has(String(c.id)));
    return [{ id: '', text: 'Select Customer' }, ...filtered];
  }, [customers, generatedTodayIds, tab]);

  const customerDropdownWidth = useMemo(() => {
    const longest = reportCustomerOptions.reduce((max, c) => Math.max(max, (c.text || '').length), 'Select Customer'.length);
    return Math.min(Math.max(longest * 7 + 40, 120), 260);
  }, [reportCustomerOptions]);

  const { currentGroups, releasedGroups, currentCount, releasedCount, displayGroups, displayCount } = useMemo(() => {
    const current = [];
    const released = [];
    let currentCount = 0;
    let releasedCount = 0;
    for (const g of groups) {
      const cRows = (g.rows || []).filter(r => !r.is_released);
      const rRows = (g.rows || []).filter(r => r.is_released);
      if (cRows.length) current.push({ ...g, rows: cRows });
      if (rRows.length) released.push({ ...g, rows: rRows });
      currentCount += cRows.length;
      releasedCount += rRows.length;
    }
    const displayGroups = tab === 'current' ? current : (tab === 'released' ? released : []);
    const displayCount = tab === 'current' ? currentCount : (tab === 'released' ? releasedCount : 0);
    return { currentGroups: current, releasedGroups: released, currentCount, releasedCount, displayGroups, displayCount };
  }, [groups, tab]);

  const orderedFieldDefs = useMemo(() => {
    const selected = configFields.map(key => allFieldDefs.find(f => f.key === key)).filter(Boolean);
    const remaining = allFieldDefs.filter(f => !configFields.includes(f.key));
    return [...selected, ...remaining];
  }, [allFieldDefs, configFields]);

  const renderTableHeader = (group, groupFields) => (
    <thead>
      <tr>
        {groupFields.map(f => (
          <th key={f.key} style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{getFieldLabel(f, group)}</th>
        ))}
      </tr>
    </thead>
  );

  return (
    <div className="client-reports-page" style={{ padding: '16px 16px 0', overflowX: 'auto' }}>
      <div className="list-header">
        <h2 style={{ margin: 0, color: '#333' }}>Client Reports</h2>
      </div>

      <div className="toolbar" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <div style={{ width: customerDropdownWidth, minWidth: customerDropdownWidth }}>
            <Select2Dropdown
              options={reportCustomerOptions}
              value={customerId}
              onChange={handleCustomerChange}
              valueKey="id"
              displayKey="text"
              placeholder="Select Customer"
            />
          </div>
          <input
            className="search-input"
            placeholder="Search BL, IDF, Job ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleApplySearch(); }}
            style={{ width: 220, minWidth: 220 }}
          />
          <button className="add-btn" style={{ background: '#007bff' }} onClick={handleApplySearch}>Search</button>
          <button className="add-btn" style={{ background: '#6c757d' }} onClick={handleClearFilters}>Clear All</button>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="add-btn" style={{ background: '#007bff' }} onClick={handleRefresh}>Refresh</button>
          <button className="add-btn" style={{ background: '#e67e22' }} onClick={handlePreviewDailyReport} disabled={previewLoading || generateLoading || !customerId || tab === 'dailyReport'}>
            {previewLoading ? 'Loading Preview...' : 'Preview Daily Report'}
          </button>
          <button className="add-btn" style={{ background: '#3f6b32' }} onClick={handleGenerateDailyReport} disabled={generateLoading || previewLoading || !customerId || tab === 'dailyReport'}>
            {generateLoading ? 'Generating...' : 'Generate Daily Report'}
          </button>
          {canDeleteClientReport && (
            <button
              className="add-btn"
              style={{ background: '#17a2b8', padding: '6px 12px' }}
              onClick={openConfig}
              title="Configure Client Report Fields"
            >
              <i className="fa-solid fa-gear" style={{ marginRight: 6 }} />Configure
            </button>
          )}
        </div>
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 12 }}>
        <button
          className={`custom-tab ${tab === 'current' ? 'active' : ''}`}
          onClick={() => setTab('current')}
        >
          Current {currentCount !== null && `(${currentCount})`}
        </button>
        <button
          className={`custom-tab ${tab === 'released' ? 'active' : ''}`}
          onClick={() => setTab('released')}
        >
          Released {releasedCount !== null && `(${releasedCount})`}
        </button>
        <button
          className={`custom-tab ${tab === 'dailyReport' ? 'active' : ''}`}
          onClick={() => setTab('dailyReport')}
        >
          Daily Report {dailyReportCount !== null && `(${dailyReportCount})`}
        </button>
      </div>

      <div style={{ display: tab === 'dailyReport' ? 'block' : 'none' }}>
        <DailyClientReportsTab customerId={customerId} hasPermission={hasPermission} onCountChange={setDailyReportCount} refreshTrigger={refreshTrigger} canDeleteClientReport={canDeleteClientReport} />
      </div>

      {tab !== 'dailyReport' && (
        <>
          {loading && <div className="loading-indicator">Loading...</div>}

          {!loading && displayGroups.length === 0 && (
            <div className="no-data" style={{ padding: 24, textAlign: 'center', background: '#f8f9fa', borderRadius: 6 }}>
              {customerId || appliedSearch ? 'No jobs found for the selected criteria.' : 'Select a customer or enter BL/IDF/Job ID to view the report.'}
            </div>
          )}

          {!loading && displayGroups.map((group) => {
            const groupFields = getVisibleFields(fields, group.shipment_type);
            return (
            <div key={group.shipment_type} style={{ marginBottom: 24 }}>
              <div style={{
                background: '#e5def1',
                padding: '8px 12px',
                borderRadius: '6px 6px 0 0',
                fontWeight: 'bold',
                fontSize: 14,
                color: '#333'
              }}>
                {group.shipment_type || 'OTHER'} ({group.rows?.length || 0})
              </div>
              <TableScrollContainer>
                <table className="data-table" style={{ width: '100%', minWidth: groupFields.length * 120, overflow: 'visible' }}>
                  {renderTableHeader(group, groupFields)}
                  <tbody>
                    {group.rows.map((row, idx) => {
                      const draggableRow = canDragRows && !!getRowSortEndpoint(group.shipment_type);
                      return (
                        <tr
                          key={row.id ?? idx}
                          draggable={draggableRow}
                          onDragStart={draggableRow ? (e) => handleRowDragStart(e, row.id) : undefined}
                          onDragOver={draggableRow ? (e) => handleRowDragOver(e, row.id) : undefined}
                          onDrop={draggableRow ? (e) => handleRowDrop(e, group, row.id) : undefined}
                          onDragEnd={draggableRow ? handleRowDragEnd : undefined}
                          style={{
                            cursor: draggableRow ? 'grab' : undefined,
                            ...(rowDragOverId === row.id && rowDragSrcId !== row.id
                              ? { outline: '2px dashed #3f6b32', backgroundColor: '#e8f5e9' }
                              : {}),
                            ...(rowDragSrcId === row.id ? { opacity: 0.5 } : {}),
                          }}
                        >
                          {groupFields.map(f => (
                            <td key={f.key} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                              <CopyCell text={toDisplay(row[f.key])}>{renderCell(row, f)}</CopyCell>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {(!group.rows || group.rows.length === 0) && (
                      <tr><td colSpan={groupFields.length || 1} className="no-data">No records</td></tr>
                    )}
                  </tbody>
                </table>
              </TableScrollContainer>
            </div>
            );
          })}
        </>
      )}

      {showConfig && (
        <div className="modal-overlay" onClick={closeConfig}>
          <div className="dialog-container" style={{ width: 520, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Configure Client Report Fields</h3>
              <button className="close-btn" onClick={closeConfig}>&times;</button>
            </div>
            <div className="dialog-body" style={{ padding: 16, overflowY: 'auto' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Client</label>
                <Select2Dropdown
                  options={customerOptions}
                  value={configCustomerId}
                  onChange={handleConfigCustomerChange}
                  valueKey="id"
                  displayKey="text"
                  placeholder="Select Customer"
                  style={{ minWidth: '100%' }}
                />
              </div>

              <div style={{ marginBottom: 10, fontWeight: 600 }}>Selected Fields &amp; Order</div>
              <div style={{ border: '1px solid #ddd', borderRadius: 6, maxHeight: 360, overflowY: 'auto' }}>
                {orderedFieldDefs.map((f) => {
                  const selected = configFields.includes(f.key);
                  return (
                    <div
                      key={f.key}
                      style={{
                        display: 'flex', alignItems: 'center',
                        padding: '8px 12px', borderBottom: '1px solid #eee',
                        background: selected ? (dragOverKey === f.key ? '#d0eaff' : '#f0f7ff') : '#fff',
                        opacity: dragSrcKey === f.key ? 0.4 : 1,
                      }}
                      onDragOver={selected ? (e) => handleDragOver(e, f.key) : undefined}
                      onDrop={selected ? (e) => handleDrop(e, f.key) : undefined}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                        <input type="checkbox" checked={selected} onChange={() => toggleField(f.key)} />
                        <span>{f.label}</span>
                      </label>
                      {selected ? (
                        <span
                          draggable
                          onDragStart={(e) => handleDragStart(e, f.key)}
                          onDragEnd={handleDragEnd}
                          style={{ cursor: 'grab', paddingLeft: 10, color: '#666' }}
                          title="Drag to reorder"
                        >
                          <i className="fa-solid fa-grip-vertical" />
                        </span>
                      ) : (
                        <span style={{ paddingLeft: 10, color: '#ccc' }}>
                          <i className="fa-solid fa-grip-vertical" />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="dialog-footer" style={{ padding: '12px 16px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="add-btn" style={{ background: '#6c757d' }} onClick={closeConfig}>Cancel</button>
              <button className="add-btn" style={{ background: '#3f6b32' }} onClick={handleSaveConfig} disabled={savingConfig || !configCustomerId}>
                {savingConfig ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReports;
